// File System Access layer: the user picks a folder, the browser keeps the
// handle, and the app re-attaches to it on later visits.
//
// Two rules this module exists to enforce:
//  * the directory is always chosen by the user through the browser's own
//    picker. No default path, no guessing where the game is installed, nothing
//    platform specific.
//  * access is requested read-only, so the app can never touch a game file.

import { getMeta, setMeta, deleteMeta } from './db'

const HANDLE_KEY = 'directory-handle'
const DIRECTORY_META_KEY = 'directory-meta'
const PICKER_ID = 'rio-dashboard-combat-logs'
const LOG_NAME_PATTERN = /^WoWCombatLog.*\.txt$/i
const TEXT_NAME_PATTERN = /\.txt$/i

export function supportsDirectoryAccess() {
    return typeof window !== 'undefined'
        && typeof window.showDirectoryPicker === 'function'
        && typeof FileSystemDirectoryHandle !== 'undefined'
}

export function supportsFileSystemObserver() {
    return typeof window !== 'undefined' && typeof window.FileSystemObserver === 'function'
}

export function supportsHandlePersistence() {
    return typeof indexedDB !== 'undefined' && supportsDirectoryAccess()
}

function createDirectoryId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `dir-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export async function pickDirectory() {
    if (!supportsDirectoryAccess()) {
        throw new Error('This browser cannot open a local folder')
    }

    // `id` lets the browser reopen the picker where the user left it. No
    // `startIn` path is passed: where the logs live is the user's choice.
    const handle = await window.showDirectoryPicker({ id: PICKER_ID, mode: 'read' })
    const meta = {
        directoryId: createDirectoryId(),
        name: handle.name,
        connectedAt: Date.now(),
    }

    await setMeta(HANDLE_KEY, handle)
    await setMeta(DIRECTORY_META_KEY, meta)
    return { handle, meta }
}

export async function loadStoredDirectory() {
    if (!supportsHandlePersistence()) return null
    const handle = await getMeta(HANDLE_KEY)
    if (!handle) return null
    const meta = (await getMeta(DIRECTORY_META_KEY)) || {
        directoryId: createDirectoryId(),
        name: handle.name,
        connectedAt: Date.now(),
    }
    return { handle, meta }
}

export async function forgetDirectory() {
    await deleteMeta(HANDLE_KEY)
    await deleteMeta(DIRECTORY_META_KEY)
}

export async function queryAccess(handle) {
    if (!handle || typeof handle.queryPermission !== 'function') return 'unsupported'
    try {
        return await handle.queryPermission({ mode: 'read' })
    } catch (error) {
        return 'denied'
    }
}

// Must be called from a user gesture: browsers reject a permission prompt that
// is not tied to a click.
export async function requestAccess(handle) {
    if (!handle || typeof handle.requestPermission !== 'function') return 'unsupported'
    try {
        return await handle.requestPermission({ mode: 'read' })
    } catch (error) {
        return 'denied'
    }
}

export function isLogFileName(name) {
    return TEXT_NAME_PATTERN.test(name)
}

export function looksLikeWowLog(name) {
    return LOG_NAME_PATTERN.test(name)
}

// Reads the directory listing. Only metadata is touched here: `getFile()` is
// cheap and does not read any content.
export async function scanDirectory(handle) {
    const files = []
    let skipped = 0

    for await (const entry of handle.values()) {
        if (entry.kind !== 'file') continue
        if (!isLogFileName(entry.name)) {
            skipped += 1
            continue
        }

        try {
            const file = await entry.getFile()
            files.push({
                name: entry.name,
                size: file.size,
                lastModified: file.lastModified,
                handle: entry,
                file,
                isWowLog: looksLikeWowLog(entry.name),
            })
        } catch (error) {
            // A file can disappear between listing and opening it.
            skipped += 1
        }
    }

    files.sort((left, right) => right.lastModified - left.lastModified)
    return { files, skipped, scannedAt: Date.now() }
}

export async function getFileFromHandle(handle) {
    if (!handle || typeof handle.getFile !== 'function') return null
    try {
        return await handle.getFile()
    } catch (error) {
        return null
    }
}
